import { http, sendMessageToApp } from 'services/http';
import { AUTH } from './reducer';
import { ROLES } from 'services/constants';
import { normalizeToken } from 'services/functions';
import { push } from 'react-router-redux';
import { alertError } from 'services/alerts';

import store from '../createStore';
import { reset } from 'redux-form';
import { clearReservationStep } from 'views/patient/Reservation/reducer/actions';
import { loadPatientData } from 'src/views/patient/Profile/reducer';
import { localAppointmentsFilters } from 'views/practitioner/services/appointments';
import decoded from 'jwt-decode';
import i18n from 'src/i18n';

const toUC = value => {
  if (value) {
    return String(value).toUpperCase();
  }
  return value;
};

// ===   Login   ===
export const clearStatus = () => ({ type: AUTH.CLEAR_STATUS });

export const login = (form) => dispatch => {
  localStorage.removeItem('token');
  localStorage.removeItem('um_role');
  const isPractitionerPage = window.location.href.match('practitioner');

  return http
    .post('auth/login', { ...form })
    .then(response => {
      const dataFromToken = normalizeToken(response.data.access_token);
      const userRole = dataFromToken && dataFromToken.role && toUC(dataFromToken.role);
      localStorage.setItem('um_role', userRole);
      const successLogin = () => {
        dispatch(loginSuccess(response.data, userRole));
        dispatch(updateAccessTokenTime(dataFromToken || {}));
      };
      if (userRole === toUC(ROLES.PATIENT) && !isPractitionerPage) {
        // if patient
        dispatch(push('/dashboard/overview'));
        dispatch(loadPatientData());
        successLogin();
      } else if (
        // if practitioner or staff        
        (userRole === toUC(ROLES.STAFF) ||
          userRole === toUC(ROLES.PRACTITIONER) ||
          userRole === toUC(ROLES.RADIOLOGY)) && isPractitionerPage
      ) {
        dispatch(addPractitioner(dataFromToken));
        dispatch(push('/practitioner/appointments/today'));
        successLogin();
      } else {
        throw Error('error parsing token data');
      }
    })
    .catch(err => {
      const { response, message } = err;
      if (message && message.match(/Network Error/i)) {
        alertError(i18n.t('snack_bar_server_error'));
      } else if (
        response &&
        response.status !== 400 &&
        response.status !== 401 &&
        response.status !== 406
      ) {
        alertError();
      }
      else if (isPractitionerPage) {
        dispatch(loginError('error_hint_login_practitioner'));
      }
      else if (!isPractitionerPage) {
        dispatch(loginError('pass_nn_invalid_text'));
      }
    });
};

const loginError = text => {
  localStorage.removeItem('token');
  return {
    type: AUTH.LOGIN_USER_ERROR,
    payload: text
  };
};

export const loginSuccess = (tokenData, role) => {
  localStorage.setItem('token', JSON.stringify(tokenData));
  const form = store && store.getState().form ? store.getState().form : null;
  if (form && form.reservation) {
    store.dispatch(reset('reservation'));
    store.dispatch(clearReservationStep());
  }

  return {
    type: AUTH.LOGIN_USER_SUCCESS,
    payload: { data: tokenData, role }
  };
};

export const updateAccessTokenTime = accessToken => ({
  type: AUTH.UPDATE_ACCESS_TOKEN_TIME,
  payload: { exp: accessToken.exp, nbf: accessToken.nbf }
});

// ===   Logout  ===
export const logout = auth => dispatch => {
  const clearDataAndRedirect = () => {
    const role = localStorage.getItem('um_role');
    // clear
    localAppointmentsFilters.clear();
    localStorage.removeItem('token');
    // redirect
    if (role === ROLES.STAFF ||
      role === ROLES.PRACTITIONER ||
      role === ROLES.RADIOLOGY) {
      dispatch(push('/practitioner'));
    } else {
      dispatch(push('/'));
    }
  };

  return http
    .get('auth/logout', {
      params: {
        refreshToken: auth.refreshToken
      }
    })
    .then(() => {
      dispatch(logoutSuccess());
      clearDataAndRedirect();
    })
    .catch(() => {
      dispatch(logoutError());
      clearDataAndRedirect();
    });
};

const logoutSuccess = () => ({
  type: AUTH.LOGOUT_SUCCESS
});

const logoutError = () => ({
  type: AUTH.LOGOUT_ERROR
});

// ===  Tokens  ====

export const invalidToken = () => ({ type: AUTH.INVALID_TOKEN });

export const refreshTokenExpired = () => ({ type: AUTH.REFRESH_EXPIRED });

export const pendingRefreshToken = isPending => ({
  type: AUTH.PENDING_REFRESH_TOKEN,
  payload: isPending
});

export const refreshToken = refreshToken => dispatch => {
  return http
    .post(
      'auth/refreshtoken',
      {
        refreshToken
      },
      {
        withoutLoader: true
      }
    )
    .then(({ data }) => {
      dispatch(updateAccessTokenTime(decoded(data.access_token) || {}));
      dispatch(refreshTokenSuccess(data));
    })
    .catch(() => {
      dispatch(refreshTokenExpired());
      dispatch(refreshTokenError());
      dispatch(pendingRefreshToken(false));
    });
};

export const refreshTokenSuccess = data => {
  localStorage.setItem('token', JSON.stringify(data));
  // send event to mobile app
  sendMessageToApp({ actionName: 'updateToken', data: data });

  return {
    type: AUTH.REFRESH_TOKEN_SUCCESS,
    payload: data
  };
};

export const refreshTokenError = () => {
  localStorage.removeItem('token');

  return {
    type: AUTH.REFRESH_TOKEN_ERROR
  };
};

// ===   Ping  ===
export const ping = () => dispatch => {
  return http
    .get('auth/ping')
    .then(() => {
      confirm('Some action');
    })
    .catch(() => {
      dispatch(invalidToken());
    });
};

export const addPractitioner = practitioner => ({
  type: AUTH.ADD_PRACTITIONER,
  payload: practitioner
});
